import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import StatusPill from "@/components/StatusPill";
import { fmtDate, fmtDateTime, titleCase } from "@/lib/format";
import { MapPin, Truck as TruckIcon, Radio, Wifi, WifiOff } from "lucide-react";
import { openDispatchSocket } from "@/lib/dispatchSocket";
import { loadGoogleMaps } from "@/lib/gmaps";

export default function Dispatch() {
    const [trucks, setTrucks] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [liveLocations, setLiveLocations] = useState({});  // truck_id -> {lat,lng,updated_at}
    const [wsConnected, setWsConnected] = useState(false);
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markersRef = useRef({});

    useEffect(() => {
        Promise.all([
            api.get("/fleet/trucks"),
            api.get("/routes"),
            api.get("/rolloff?status=scheduled"),
        ]).then(([t, r, j]) => {
            setTrucks(t.data); setRoutes(r.data); setJobs(j.data);
            // Hydrate initial locations from truck docs
            const init = {};
            t.data.forEach((tr) => { if (tr.location?.lat) init[tr.id] = tr.location; });
            setLiveLocations(init);
        });
    }, []);

    // WebSocket subscription
    useEffect(() => {
        const close = openDispatchSocket((msg) => {
            if (msg.type === "init") {
                setLiveLocations((prev) => ({ ...prev, ...(msg.trucks || {}) }));
                setWsConnected(true);
            }
            if (msg.type === "truck_update") {
                setLiveLocations((prev) => ({ ...prev, [msg.truck_id]: msg.location }));
            }
        });
        // Detect connection by short delay
        const t = setTimeout(() => setWsConnected(true), 500);
        return () => { close(); clearTimeout(t); setWsConnected(false); };
    }, []);

    // Google Maps
    useEffect(() => {
        let cancelled = false;
        loadGoogleMaps().then((google) => {
            if (cancelled || !mapRef.current) return;
            mapInstance.current = new google.maps.Map(mapRef.current, {
                center: { lat: 41.32, lng: -72.35 },
                zoom: 11,
                disableDefaultUI: true,
                zoomControl: true,
                styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }],
            });
        }).catch(() => {});
        return () => { cancelled = true; };
    }, []);

    // Sync markers when liveLocations change
    useEffect(() => {
        if (!mapInstance.current || !window.google) return;
        const google = window.google;
        Object.entries(liveLocations).forEach(([truckId, loc]) => {
            if (!loc?.lat) return;
            if (markersRef.current[truckId]) {
                markersRef.current[truckId].setPosition({ lat: loc.lat, lng: loc.lng });
            } else {
                const truck = trucks.find((t) => t.id === truckId);
                markersRef.current[truckId] = new google.maps.Marker({
                    position: { lat: loc.lat, lng: loc.lng },
                    map: mapInstance.current,
                    title: truck?.name || truckId,
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 8,
                        fillColor: "#0066ff",
                        fillOpacity: 1,
                        strokeColor: "#fff",
                        strokeWeight: 2,
                    },
                });
            }
        });
        // Remove markers for trucks no longer in liveLocations
        Object.keys(markersRef.current).forEach((id) => {
            if (!liveLocations[id]) {
                markersRef.current[id].setMap(null);
                delete markersRef.current[id];
            }
        });
    }, [liveLocations, trucks]);

    const liveCount = Object.keys(liveLocations).length;

    return (
        <div data-testid="dispatch-page">
            <PageHeader
                title="Dispatch Center"
                subtitle="Live truck locations, route progress and field activity in one console."
                actions={
                    <div className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full ${
                        wsConnected ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                                    : "bg-amber-500/10 text-amber-600 dark:text-amber-400"
                    }`} data-testid="dispatch-ws-status">
                        {wsConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                        {wsConnected ? `Live · ${liveCount} truck${liveCount === 1 ? "" : "s"}` : "Connecting…"}
                    </div>
                }
            />
            <div className="px-8 py-6 grid lg:grid-cols-3 gap-4">
                <Card className="lg:col-span-2">
                    <CardContent className="p-0">
                        <div className="p-5 border-b border-border flex items-center justify-between">
                            <div>
                                <div className="overline mb-1">Live map</div>
                                <h3 className="font-heading text-lg font-semibold">Field operations — {fmtDate(new Date().toISOString())}</h3>
                            </div>
                            <div className="text-xs text-muted-foreground font-mono flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 pin-pulse" /> Live
                            </div>
                        </div>
                        <div ref={mapRef} className="h-[520px] w-full" data-testid="dispatch-map" />
                    </CardContent>
                </Card>

                <div className="space-y-4">
                    <Card data-testid="trucks-status-card">
                        <CardContent className="p-5">
                            <div className="overline mb-1">Fleet</div>
                            <h3 className="font-heading text-lg font-semibold mb-4">Trucks in field</h3>
                            <div className="space-y-2">
                                {trucks.map((t) => {
                                    const live = liveLocations[t.id];
                                    return (
                                        <div key={t.id} className="flex items-center gap-3 p-2.5 border border-border rounded-md">
                                            <TruckIcon className="w-4 h-4 text-muted-foreground" />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium truncate">{t.name}</div>
                                                <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                                                    {titleCase(t.type)}
                                                    {live?.updated_at && (
                                                        <>· <span className="text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 pin-pulse" />
                                                            {fmtDateTime(live.updated_at)}
                                                        </span></>
                                                    )}
                                                </div>
                                            </div>
                                            <StatusPill status={t.status} />
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>

                    <Card data-testid="dispatch-routes-card">
                        <CardContent className="p-5">
                            <div className="overline mb-1">Active routes</div>
                            <h3 className="font-heading text-lg font-semibold mb-4">Today + tomorrow</h3>
                            <div className="space-y-2">
                                {routes.slice(0, 5).map((r) => (
                                    <div key={r.id} className="p-2.5 border border-border rounded-md">
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="text-sm font-medium">{r.name}</div>
                                            <StatusPill status={r.status} />
                                        </div>
                                        <div className="text-[11px] text-muted-foreground flex items-center gap-3">
                                            <span><MapPin className="w-3 h-3 inline mr-0.5" />{r.zone}</span>
                                            <span>{r.stops?.length || 0} stops</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    <Card data-testid="dispatch-jobs-card">
                        <CardContent className="p-5">
                            <div className="overline mb-1">Roll-off queue</div>
                            <h3 className="font-heading text-lg font-semibold mb-4">Next up</h3>
                            <div className="space-y-2">
                                {jobs.slice(0, 4).map((j) => (
                                    <div key={j.id} className="p-2.5 border border-border rounded-md">
                                        <div className="flex items-center justify-between mb-0.5">
                                            <div className="text-sm font-medium truncate">{j.customer_name}</div>
                                            <Radio className="w-3 h-3 text-muted-foreground" />
                                        </div>
                                        <div className="text-[11px] text-muted-foreground">{titleCase(j.job_type)} · {fmtDate(j.scheduled_date)}</div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
